import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Loading } from "@/components/atoms";

// Type definitions for Kinde claims
type UserProperty = { v: string };
type ClaimsWithUserProperties = {
  user_properties?: {
    kp_usr_utm_content?: UserProperty;
  };
};

/**
 * Route guard for operator onboarding
 * Ensures only users with utm_content="operator" can access operator onboarding
 * Redirects clients to client onboarding
 * Logs out user_account users (they shouldn't be here - their access may have been revoked)
 */
export const RequireOperatorOnboarding = () => {
  const { getClaims, logout } = useKindeAuth();
  const [userType, setUserType] = useState<string | null>(null);

  useEffect(() => {
    getClaims("idToken")
      .then((claims) => {
        const typedClaims = claims as ClaimsWithUserProperties;
        const content = typedClaims?.user_properties?.kp_usr_utm_content?.v;
        setUserType(content || "operator"); // Default to operator if undefined
      })
      .catch((error) => {
        console.error("Error fetching Kinde claims:", error);
        // Default to operator if we can't determine
        setUserType("operator");
      });
  }, [getClaims]);

  // Logout user_account users - their access may have been revoked
  useEffect(() => {
    if (userType === "user_account") {
      logout();
    }
  }, [userType, logout]);

  // Still loading claims
  if (userType === null) {
    return <Loading />;
  }

  // user_account - show loading while logout is in progress
  if (userType === "user_account") {
    return <Loading />;
  }

  // Driver trying to access onboarding - redirect to driver app page
  if (userType === "driver") {
    return <Navigate to="/driver-app" replace />;
  }

  // Client user, redirect to client onboarding
  if (userType === "client") {
    return <Navigate to="/onboarding/client" replace />;
  }

  // Is an operator, allow access
  return <Outlet />;
};
